/**
 * Canonical workflow parameter types + alias normalization.
 *
 * Why: the code generator expects exact BackEndValueType strings (e.g. "Int32").
 * LLMs often emit friendly names ("Integer", "Float", "Bool") that silently
 * corrupt the workflow JSON and blow up at codegen time. Normalize at the
 * entry boundary (DSL parser + action functions) so invalid types are either
 * fixed or rejected with a clear message — never persisted.
 *
 * Source of truth: the SELECTABLE `dataTypes` list in the human-facing workflow
 * parameter editor (`work-flow-list/workflow-parameter-item-create.tsx`). That
 * list is the actual set the workflow engine supports — Boolean, DateTimeOffset,
 * Decimal, Double, Guid, Int32, Int64, Single, String. It deliberately EXCLUDES
 * `Image`, `Bytea`, `Byte`, `Int16` (commented out there): the broad
 * `BackEndValueType` enum (kuika-cl-model/src/models/workflow.ts) lists them, but
 * a workflow process parameter cannot carry them. The normalizer previously
 * mirrored the broad enum, so the AI could emit e.g. `PARAM BO ... Image` and it
 * silently passed here, then failed / rendered nothing at the engine. We now
 * accept ONLY the engine-supported set and COERCE the excluded types to their
 * nearest supported representation (an uploaded receipt/attachment is a URL
 * String, not an Image) instead of persisting an unsupported type. `Unknown`
 * stays as an internal sentinel (not user-selectable, but round-trips through
 * default-typed params).
 */

export type WorkflowParamType =
  | "Unknown"
  | "Boolean"
  | "DateTimeOffset"
  | "Decimal"
  | "Double"
  | "Guid"
  | "Int32"
  | "Int64"
  | "Single"
  | "String";

export const WORKFLOW_PARAM_TYPES: readonly WorkflowParamType[] = [
  "Unknown",
  "Boolean",
  "DateTimeOffset",
  "Decimal",
  "Double",
  "Guid",
  "Int32",
  "Int64",
  "Single",
  "String"
] as const;

/**
 * Friendly aliases the LLM (or humans) commonly use, PLUS coercions of the
 * engine-UNSUPPORTED types the broad `BackEndValueType` enum still lists — each
 * mapped to its nearest supported workflow type so an unsupported emission
 * degrades gracefully instead of persisting a type the workflow engine rejects.
 * Kept narrow and unambiguous.
 */
const ALIASES: Record<string, WorkflowParamType> = {
  // integers
  integer: "Int32",
  int: "Int32",
  long: "Int64",
  short: "Int32",
  // floats
  float: "Single",
  // booleans
  bool: "Boolean",
  // strings
  text: "String",
  str: "String",
  // date/time — BackEndValueType for workflow params only has DateTimeOffset
  datetime: "DateTimeOffset",
  date: "DateTimeOffset",
  // guid
  uuid: "Guid",
  // engine-unsupported types coerced to the nearest supported representation —
  // a file/receipt/attachment carried into a process is its uploaded URL (String),
  // Int16/Byte collapse to Int32, raw bytes to a String reference.
  image: "String",
  bytea: "String",
  byte: "Int32",
  int16: "Int32"
};

const CANONICAL_LOWER = new Map<string, WorkflowParamType>(WORKFLOW_PARAM_TYPES.map((t) => [t.toLowerCase(), t]));

export interface NormalizeSuccess {
  ok: true;
  type: WorkflowParamType;
  /** The raw input, echoed back for callers that want to report aliasing. */
  aliased: boolean;
}

export interface NormalizeFailure {
  ok: false;
  message: string;
}

export type NormalizeResult = NormalizeSuccess | NormalizeFailure;

/**
 * Accepts canonical types case-insensitively; maps common aliases to canonical.
 * Returns a parse-time error message listing valid types when the input is unknown.
 */
export function normalizeWorkflowParamType(raw: string | undefined | null): NormalizeResult {
  const trimmed = (raw ?? "").trim();
  if (!trimmed) {
    return { ok: false, message: `Parameter type is required. Valid types: ${WORKFLOW_PARAM_TYPES.join(", ")}.` };
  }
  const lower = trimmed.toLowerCase();

  const canonical = CANONICAL_LOWER.get(lower);
  if (canonical) {
    return { ok: true, type: canonical, aliased: canonical !== trimmed };
  }

  const aliased = ALIASES[lower];
  if (aliased) {
    return { ok: true, type: aliased, aliased: true };
  }

  return {
    ok: false,
    message: `Unknown parameter type "${trimmed}". Valid types: ${WORKFLOW_PARAM_TYPES.join(
      ", "
    )}. Common aliases accepted: Integer/Int → Int32, Long → Int64, Short → Int16, Float → Single, Bool → Boolean, Text → String, DateTime → DateTimeOffset, UUID → Guid.`
  };
}
